import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Upload, Loader, Download, AlertCircle } from 'lucide-react';
import MethodSubjectField from '@/components/MethodSubjectField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicAdmissionForm = () => {
  const { tenantId } = useParams();
  console.log('PublicAdmissionForm tenantId from URL:', tenantId);
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [states, setStates] = useState([]);
  const [currentDistricts, setCurrentDistricts] = useState([]);
  const [permanentDistricts, setPermanentDistricts] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantInfoLoading, setTenantInfoLoading] = useState(true);
  const [tenantError, setTenantError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [applicationData, setApplicationData] = useState(null);
  const [documentSettings, setDocumentSettings] = useState([]);
  const [documentSettingsLoading, setDocumentSettingsLoading] = useState(false);
  const [religions, setReligions] = useState([]);
  const [castes, setCastes] = useState([]);
  const [formData, setFormData] = useState({
    // Personal Details
    first_name: '',
    middle_name: '',
    last_name: '',
    roll_number: '',
    university_id: '',
    method_subject: '',
    email: '',
    phone: '',
    whatsapp_no: '',
    date_of_birth: '',
    gender: 'Male',
    religion: '',
    caste_category: '',
    blood_group: '',
    nationality: 'Indian',
    aadhar_no: '',
    
    // Address
    address: '',
    city: '',
    state: '',
    district: '',
    pin_code: '',
    landmark: '',
    
    // Permanent Address
    same_as_current: true,
    permanent_address: '',
    permanent_city: '',
    permanent_state: '',
    permanent_district: '',
    permanent_pin_code: '',
    permanent_landmark: '',
    
    // Academic - 10th
    class_10_board: '',
    class_10_school: '',
    class_10_year: '',
    class_10_percentage: '',
    
    // Academic - 12th
    class_12_board: '',
    class_12_school: '',
    class_12_year: '',
    class_12_percentage: '',
    
    // Diploma (for lateral entry)
    diploma_name: '',
    diploma_college: '',
    diploma_board: '',
    diploma_year: '',
    diploma_percentage: '',
    
    // Gap Year
    gap_year: '',
    gap_reason: '',
    
    // Higher Education (Optional)
    has_graduation: false,
    graduation_degree: '',
    graduation_college: '',
    graduation_university: '',
    graduation_year: '',
    graduation_percentage: '',
    
    has_masters: false,
    masters_degree: '',
    masters_college: '',
    masters_university: '',
    masters_year: '',
    masters_percentage: '',
    
    // Medium of Instruction
    medium_of_instruction: 'English',
    
    // Parents
    father_name: '',
    father_phone: '',
    father_email: '',
    father_occupation: '',
    mother_name: '',
    mother_phone: '',
    mother_email: '',
    mother_occupation: '',
    
    // Guardian
    guardian_name: '',
    guardian_phone: '',
    guardian_relation: 'Father',
    
    // Course
    course_id: '',
    is_lateral_entry: false,
    entry_semester: null,
    session: '',  // Empty by default - will be set when course is selected
    
    // Documents
    photo_url: '',
    documents: {}
  });

  useEffect(() => {
    console.log('PublicAdmissionForm useEffect, tenantId:', tenantId);
    if (tenantId) {
      fetchTenantInfo();
    } else {
      setTenantInfoLoading(false);
    }
    fetchCourses();
    fetchSessions();
    fetchStates();
    fetchReligions();
    fetchCastes();
    loadRazorpayScript();
  }, [tenantId]);

  const fetchTenantInfo = async () => {
    console.log('Fetching tenant info for:', tenantId);
    setTenantInfoLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/online-admissions/public/tenant-info/${tenantId}`);
      console.log('Tenant info response:', response.data);
      setTenantInfo(response.data);
      setTenantError(false);
    } catch (error) {
      console.error('Error fetching tenant info:', error);
      setTenantError(true);
    } finally {
      setTenantInfoLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const url = tenantId 
        ? `${API_URL}/api/online-admissions/public/courses?tenant_id=${tenantId}`
        : `${API_URL}/api/online-admissions/public/courses`;
      const response = await axios.get(url);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const url = tenantId 
        ? `${API_URL}/api/online-admissions/public/sessions?tenant_id=${tenantId}`
        : `${API_URL}/api/online-admissions/public/sessions`;
      const response = await axios.get(url);
      setSessions(response.data);
      // DON'T auto-select session here - it should only be selected when course is selected
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/online-admissions/public/states`);
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchReligions = async () => {
    try {
      const url = tenantId 
        ? `${API_URL}/api/online-admissions/public/religions?tenant_id=${tenantId}`
        : `${API_URL}/api/online-admissions/public/religions`;
      const response = await axios.get(url);
      setReligions(response.data);
    } catch (error) {
      console.error('Error fetching religions:', error);
    }
  };

  const fetchCastes = async () => {
    try {
      const url = tenantId 
        ? `${API_URL}/api/online-admissions/public/castes?tenant_id=${tenantId}`
        : `${API_URL}/api/online-admissions/public/castes`;
      const response = await axios.get(url);
      setCastes(response.data);
    } catch (error) {
      console.error('Error fetching castes:', error);
    }
  };

  const fetchDistricts = async (state, isPermanent = false) => {
    try {
      const response = await axios.get(`${API_URL}/api/online-admissions/public/districts/${encodeURIComponent(state)}`);
      if (isPermanent) {
        setPermanentDistricts(response.data);
      } else {
        setCurrentDistricts(response.data);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  // Fetch document settings for selected course
  const fetchDocumentSettings = async (courseId) => {
    if (!courseId || !tenantId) {
      console.log('Cannot fetch document settings - missing courseId or tenantId');
      return;
    }
    
    setDocumentSettingsLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/document-settings/public/${courseId}?tenant_id=${tenantId}`
      );
      setDocumentSettings(response.data || []);
      console.log('Document settings loaded:', response.data?.length || 0, 'documents');
    } catch (error) {
      console.error('Error fetching document settings:', error);
      // Fallback to default documents if API fails
      setDocumentSettings([]);
    } finally {
      setDocumentSettingsLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Fetch districts when state changes
    if (name === 'state' && value) {
      fetchDistricts(value, false);
      // Reset district when state changes
      setFormData(prev => ({ ...prev, district: '' }));
    }

    if (name === 'permanent_state' && value) {
      fetchDistricts(value, true);
      // Reset district when state changes
      setFormData(prev => ({ ...prev, permanent_district: '' }));
    }

    // Auto-fill permanent address if same as current
    if (name === 'same_as_current' && checked) {
      setFormData(prev => ({
        ...prev,
        permanent_address: prev.address,
        permanent_city: prev.city,
        permanent_state: prev.state,
        permanent_district: prev.district,
        permanent_pin_code: prev.pin_code,
        permanent_landmark: prev.landmark
      }));
      // Also fetch districts for permanent address
      if (formData.state) {
        fetchDistricts(formData.state, true);
      }
    }

    // Fetch document settings and auto-select session when course changes
    if (name === 'course_id' && value) {
      fetchDocumentSettings(value);
      // Auto-select active session for this course
      fetchSessionForCourse(value);
    }
  };

  // Fetch and show ONLY the sessions linked to this course, then auto-select
  const fetchSessionForCourse = async (courseId) => {
    try {
      const url = tenantId 
        ? `${API_URL}/api/online-admissions/public/sessions?tenant_id=${tenantId}&course_id=${courseId}`
        : `${API_URL}/api/online-admissions/public/sessions?course_id=${courseId}`;
      const response = await axios.get(url);
      const courseSessions = Array.isArray(response.data) ? response.data : [];
      if (courseSessions.length > 0) {
        setSessions(courseSessions);
        // Auto-select: prefer current session in the filtered list, else the first
        const preferred = courseSessions.find(s => s.is_current) || courseSessions[0];
        setFormData(prev => ({ ...prev, session: preferred.name }));
        console.log('✅ Sessions filtered for course:', courseSessions.length, '| auto-selected:', preferred.name);
      } else {
        // Fallback: Select current session from loaded sessions
        selectCurrentSession();
      }
    } catch (error) {
      console.error('Error fetching sessions for course:', error);
      // Fallback: Select current session from loaded sessions
      selectCurrentSession();
    }
  };

  // Helper: Select current session from loaded sessions
  const selectCurrentSession = () => {
    // First try to find is_current session
    const currentSession = sessions.find(s => s.is_current);
    if (currentSession) {
      setFormData(prev => ({ ...prev, session: currentSession.name }));
      console.log('✅ Fallback: Selected current session:', currentSession.name);
    } else if (sessions.length > 0) {
      // Fallback to first session
      setFormData(prev => ({ ...prev, session: sessions[0].name }));
      console.log('✅ Fallback: Selected first session:', sessions[0].name);
    }
  };

  // State for upload errors
  const [uploadErrors, setUploadErrors] = useState({});
  
  // Default file size limits (in bytes) - used when no tenant settings
  const DEFAULT_PHOTO_MAX_SIZE = 70 * 1024; // 70 KB for photo & signature
  const DEFAULT_DOC_MAX_SIZE = 300 * 1024; // 300 KB for other documents
  
  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };
  
  // Get max file size - uses tenant's document settings if available, otherwise defaults
  const getMaxFileSize = (fieldName, docSettings = null) => {
    // If document settings provided from tenant config, use that
    if (docSettings && docSettings.max_size_kb) {
      const sizeInBytes = docSettings.max_size_kb * 1024;
      return { size: sizeInBytes, label: `${docSettings.max_size_kb} KB` };
    }
    
    // Fallback to defaults based on document type
    const photoSignatureFields = ['profile_photo', 'signature', 'photo', 'passport_photo'];
    const fieldLower = fieldName.toLowerCase();
    if (photoSignatureFields.some(f => fieldLower.includes(f) || fieldLower.includes('photo') || fieldLower.includes('signature'))) {
      return { size: DEFAULT_PHOTO_MAX_SIZE, label: '70 KB' };
    }
    return { size: DEFAULT_DOC_MAX_SIZE, label: '300 KB' };
  };

  const handleFileUpload = async (e, fieldName, docSettings = null) => {
    const file = e.target.files[0];
    if (!file) return;

    // Clear previous error for this field
    setUploadErrors(prev => ({ ...prev, [fieldName]: null }));

    // Get max size - from tenant settings or defaults
    const { size: maxSize, label: maxSizeLabel } = getMaxFileSize(fieldName, docSettings);

    // Check file size
    if (file.size > maxSize) {
      const errorMsg = `File size too large! Your file: ${formatFileSize(file.size)}. Maximum allowed: ${maxSizeLabel}. Please compress or resize the file.`;
      setUploadErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
      // Clear the file input
      e.target.value = '';
      return;
    }

    // Check file type (allow images and PDFs)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = `Invalid file type: ${file.type}. Allowed: JPG, PNG, WEBP, PDF`;
      setUploadErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
      e.target.value = '';
      return;
    }

    // Convert to base64 — auto-compresses images via readFileSmart, passes PDFs through.
    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file);
      setFormData(prev => ({
        ...prev,
        documents: { ...prev.documents, [fieldName]: result.data }
      }));
      setUploadErrors(prev => ({ ...prev, [fieldName]: null }));
      if (result.compressed) {
        console.log(`✓ ${fieldName} compressed: ${(file.size/1024).toFixed(0)}KB → ${(result.size/1024).toFixed(0)}KB`);
      }
    } catch (err) {
      console.error('File read failed:', err);
      setUploadErrors(prev => ({ ...prev, [fieldName]: 'Failed to read file. Please try again.' }));
    }
  };

  const validateStep = () => {
    if (step === 1) {
      const required = ['first_name', 'last_name', 'email', 'phone', 'whatsapp_no', 'date_of_birth', 'address', 'city', 'state'];
      for (let field of required) {
        if (!formData[field]) {
          alert(`Please fill ${field.replace(/_/g, ' ')}`);
          return false;
        }
      }
    } else if (step === 2) {
      if (!formData.course_id) {
        alert('Please select a course');
        return false;
      }
      if (!formData.session) {
        alert('Please select a session');
        return false;
      }
      const required = ['class_10_board', 'class_10_percentage', 'class_12_board', 'class_12_percentage'];
      for (let field of required) {
        if (!formData[field]) {
          alert(`Please fill ${field.replace(/_/g, ' ')}`);
          return false;
        }
      }
    } else if (step === 3) {
      // Validate MANDATORY documents from Document Settings
      if (documentSettings.length > 0) {
        // Use dynamic document settings
        const mandatoryDocs = documentSettings.filter(doc => doc.is_mandatory);
        for (let doc of mandatoryDocs) {
          const docKey = doc.document_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          if (!formData.documents[docKey]) {
            alert(`Please upload ${doc.document_name} (Mandatory)`);
            return false;
          }
        }
      } else {
        // Fallback to default validation if no document settings
        const mandatoryDocs = [
          { key: 'profile_photo', label: 'Profile Photo' },
          { key: 'signature', label: 'Signature' }
        ];
        
        for (let doc of mandatoryDocs) {
          if (!formData.documents[doc.key]) {
            alert(`Please upload ${doc.label} (Mandatory)`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setSubmitting(true);
    try {
      const selectedCourse = courses.find(c => c.id === formData.course_id);
      const submitData = {
        ...formData,
        course_name: selectedCourse?.name || ''
      };
      
      // Log documents being submitted
      console.log('📄 Submitting documents:', Object.keys(submitData.documents || {}));
      console.log('📄 Documents data:', submitData.documents);

      const response = await axios.post(`${API_URL}/api/online-admissions/submit`, submitData);
      
      if (response.data.success) {
        // Merge response data with form data to preserve all information
        const fullName = [formData.first_name, formData.middle_name, formData.last_name]
          .filter(Boolean)
          .join(' ')
          .trim();
        
        setApplicationData({
          ...response.data,
          // Preserve form data for receipt generation
          full_name: fullName,
          course_name: selectedCourse?.name || response.data.course_name || '',
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          date_of_birth: formData.date_of_birth,
          session: formData.session,
          is_lateral_entry: formData.is_lateral_entry
        });
        
        // Check if payment is required
        if (response.data.payment_required === false) {
          // Payment not required - show success directly
          setStep(5); // Skip to success step
        } else {
          // Move to payment step
          setStep(4);
        }
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    setPaymentProcessing(true);
    
    try {
      // Create order - use booking_fee (not admission_fee) for online payment
      const paymentAmount = applicationData.booking_fee || applicationData.payment_amount || applicationData.admission_fee;
      const orderResponse = await axios.post(`${API_URL}/api/online-admissions/create-payment-order`, {
        application_id: applicationData.application_id,
        amount: paymentAmount
      });

      const { order_id, amount } = orderResponse.data;

      // Demo mode - skip actual Razorpay and directly verify
      if (orderResponse.data.demo_mode) {
        // Simulate payment success
        setTimeout(async () => {
          await verifyPayment({
            razorpay_order_id: order_id,
            razorpay_payment_id: `pay_demo_${Date.now()}`,
            razorpay_signature: 'demo_signature'
          });
        }, 1000);
        return;
      }

      // Real Razorpay flow
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_demo_key',
        amount: amount,
        currency: 'INR',
        name: 'College Admission',
        description: 'Admission Fee Payment',
        order_id: order_id,
        handler: async function (response) {
          await verifyPayment(response);
        },
        prefill: {
          name: `${formData.first_name} ${formData.last_name}`,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#2563eb'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  const verifyPayment = async (paymentResponse) => {
    try {
      const response = await axios.post(`${API_URL}/api/online-admissions/verify-payment`, {
        application_id: applicationData.application_id,
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature
      });

      if (response.data.success) {
        // Preserve all existing data and merge with new response
        setApplicationData(prevData => ({
          ...prevData,
          ...response.data.application,
          // Keep previously stored form data
          full_name: prevData.full_name,
          course_name: prevData.course_name,
          first_name: prevData.first_name,
          last_name: prevData.last_name,
          email: prevData.email,
          phone: prevData.phone,
          date_of_birth: prevData.date_of_birth,
          session: prevData.session,
          is_lateral_entry: prevData.is_lateral_entry,
          // Add new data from payment verification
          generated_student_id: response.data.generated_student_id,
          student_password: response.data.student_password,
          receipt_number: response.data.application?.receipt_number || prevData.receipt_number
        }));
        setStep(5); // Success step
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      alert('Payment verification failed');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const downloadReceipt = async () => {
    try {
      // Find selected course name
      const selectedCourse = courses.find(c => c.id === formData.course_id || c.id === applicationData?.course_id);
      const courseName = selectedCourse?.name || applicationData?.course_name || formData.course_name || 'N/A';
      
      // Build full name from parts
      const fullName = applicationData?.full_name || 
        [formData.first_name, formData.middle_name, formData.last_name]
          .filter(Boolean)
          .join(' ')
          .trim() || 'N/A';
      
      // Merge applicationData with formData to have complete information
      const completeData = {
        ...applicationData,
        ...formData,
        // Override with correct values
        full_name: fullName,
        course_name: courseName,
        receipt_number: applicationData?.receipt_number || applicationData?.application_number || 'N/A',
        application_number: applicationData?.application_number || 'N/A'
      };
      
      // Convert tenantInfo to collegeInfo format for receipt
      const collegeInfoForReceipt = tenantInfo ? {
        college_name: tenantInfo.name || tenantInfo.college_name,
        name: tenantInfo.name || tenantInfo.college_name,
        address: tenantInfo.address,
        city: tenantInfo.city,
        state: tenantInfo.state,
        pincode: tenantInfo.pincode || tenantInfo.pin_code,
        phone: tenantInfo.phone || tenantInfo.mobile,
        email: tenantInfo.email,
        logo_url: tenantInfo.logo_url || tenantInfo.logo
      } : null;
      
      // Generate PDF directly using the receipt generator with tenant-specific info
      const { generateOnlineAdmissionReceipt } = await import('@/utils/onlineAdmissionReceiptGenerator');
      await generateOnlineAdmissionReceipt(completeData, {}, collegeInfoForReceipt);
    } catch (error) {
      console.error('Error generating receipt:', error);
      alert('Failed to generate receipt. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with Tenant Branding */}
        <div className="text-center mb-8">
          {/* Tenant Logo and Name */}
          {tenantInfoLoading ? (
            <div className="mb-6">
              <div className="w-20 h-20 bg-gray-200 rounded-full animate-pulse mx-auto mb-4"></div>
              <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mx-auto"></div>
            </div>
          ) : tenantInfo ? (
            <div className="mb-6">
              {tenantInfo.logo_url ? (
                <img 
                  src={tenantInfo.logo_url} 
                  alt={tenantInfo.name} 
                  className="h-20 w-auto object-contain mx-auto mb-4"
                />
              ) : (
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">
                    {tenantInfo.name?.charAt(0) || 'I'}
                  </span>
                </div>
              )}
              <h2 className="text-2xl font-bold text-gray-800">{tenantInfo.name || tenantInfo.college_name}</h2>
              {tenantInfo.address && (
                <p className="text-gray-600 text-sm mt-1">{tenantInfo.address}</p>
              )}
            </div>
          ) : tenantId ? (
            <div className="mb-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">I</span>
              </div>
            </div>
          ) : null}
          
          <h1 className="text-3xl font-bold text-blue-700 mb-2">Online Admission Form</h1>
          <p className="text-gray-600">Fill out the form to apply for admission</p>
        </div>

        {/* Progress Bar */}
        {step < 5 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              {['Personal', 'Academic', 'Documents', 'Payment'].map((label, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step > index + 1 ? 'bg-green-500 text-white' :
                    step === index + 1 ? 'bg-blue-600 text-white' :
                    'bg-gray-300 text-gray-600'
                  }`}>
                    {step > index + 1 ? <CheckCircle size={20} /> : index + 1}
                  </div>
                  <span className="text-xs mt-1 text-gray-600">{label}</span>
                </div>
              ))}
            </div>
            <div className="w-full bg-gray-300 h-2 rounded-full">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 4) * 100}%` }}
              />
            </div>
          </div>
        )}

        <Card>
          <CardContent className="p-6">
            {/* Step 1: Personal Details */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800">Personal Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                    <input
                      type="text"
                      name="middle_name"
                      value={formData.middle_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Primary) *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp No. *</label>
                    <input
                      type="tel"
                      name="whatsapp_no"
                      value={formData.whatsapp_no}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nationality *</label>
                    <input
                      type="text"
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                    <select
                      name="blood_group"
                      value={formData.blood_group}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
                    <select
                      name="religion"
                      value={formData.religion}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Religion</option>
                      {religions.map(rel => (
                        <option key={rel.id} value={rel.name}>{rel.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Caste Category</label>
                    <select
                      name="caste_category"
                      value={formData.caste_category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Category</option>
                      {castes.map(caste => (
                        <option key={caste.id} value={caste.name}>{caste.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                    <input
                      type="text"
                      name="aadhar_no"
                      value={formData.aadhar_no}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="XXXX-XXXX-XXXX"
                      maxLength="12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">College Roll Number <span className="text-xs text-gray-500">(Optional)</span></label>
                    <input
                      type="text"
                      name="roll_number"
                      value={formData.roll_number || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 2025/CS/001"
                      data-testid="public-admission-roll-number-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">University ID <span className="text-xs text-gray-500">(Optional)</span></label>
                    <input
                      type="text"
                      name="university_id"
                      value={formData.university_id || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. WBUT/24/CSE/001"
                      data-testid="public-admission-university-id-input"
                    />
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mt-6">Current Address</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows="2"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">-- Select State --</option>
                        {states.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                      <select
                        name="district"
                        value={formData.district}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        disabled={!formData.state}
                      >
                        <option value="">-- Select District --</option>
                        {currentDistricts.map(district => (
                          <option key={district} value={district}>{district}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code</label>
                      <input
                        type="text"
                        name="pin_code"
                        value={formData.pin_code}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        maxLength="6"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Landmark</label>
                    <input
                      type="text"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Near City Mall, Behind School"
                    />
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mt-6">Permanent Address</h3>
                <div className="mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="same_as_current"
                      checked={formData.same_as_current}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Same as Current Address</span>
                  </label>
                </div>
                
                {!formData.same_as_current && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <textarea
                        name="permanent_address"
                        value={formData.permanent_address}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="2"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <select
                          name="permanent_state"
                          value={formData.permanent_state}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Select State --</option>
                          {states.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                        <select
                          name="permanent_district"
                          value={formData.permanent_district}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          disabled={!formData.permanent_state}
                        >
                          <option value="">-- Select District --</option>
                          {permanentDistricts.map(district => (
                            <option key={district} value={district}>{district}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          name="permanent_city"
                          value={formData.permanent_city}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code</label>
                        <input
                          type="text"
                          name="permanent_pin_code"
                          value={formData.permanent_pin_code}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          maxLength="6"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Landmark</label>
                      <input
                        type="text"
                        name="permanent_landmark"
                        value={formData.permanent_landmark}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Near City Mall, Behind School"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700">
                    Next: Academic Details →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Academic & Course Details */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800">Academic Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Course *</label>
                    <select
                      name="course_id"
                      value={formData.course_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Select Course --</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>{course.name}</option>
                      ))}
                    </select>
                  </div>

                  <MethodSubjectField
                    courseId={formData.course_id}
                    courses={courses}
                    value={formData.method_subject}
                    onChange={(val) => setFormData(prev => ({ ...prev, method_subject: val }))}
                    publicMode={true}
                    tenantSubdomain={tenantId}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Session *</label>
                    <select
                      name="session"
                      value={formData.session}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Select Session --</option>
                      {sessions.map(session => (
                        <option 
                          key={session.id} 
                          value={session.name}
                          className={session.is_current ? 'font-bold' : ''}
                        >
                          {session.is_current ? `✓ ${session.name} (Current)` : session.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2 bg-purple-50 p-3 rounded-lg">
                  {(() => {
                    const selectedCourse = courses.find(c => c.id === formData.course_id);
                    if (selectedCourse?.lateral_entry_allowed) {
                      return (
                        <>
                          <input
                            type="checkbox"
                            name="is_lateral_entry"
                            checked={formData.is_lateral_entry}
                            onChange={handleInputChange}
                            className="w-4 h-4"
                          />
                          <label className="text-sm font-medium text-gray-700">
                            I am applying for Lateral Entry (Direct {selectedCourse?.lateral_entry_semester || 3}rd Semester)
                          </label>
                        </>
                      );
                    } else if (formData.course_id) {
                      return (
                        <p className="text-sm text-gray-500">This course does not support Lateral Entry</p>
                      );
                    } else {
                      return (
                        <p className="text-sm text-gray-400">Please select a course first</p>
                      );
                    }
                  })()}
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mt-6">Class 10th Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Board *</label>
                    <input
                      type="text"
                      name="class_10_board"
                      value={formData.class_10_board}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                    <input
                      type="text"
                      name="class_10_school"
                      value={formData.class_10_school}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year of Passing</label>
                    <input
                      type="text"
                      name="class_10_year"
                      value={formData.class_10_year}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="class_10_percentage"
                      value={formData.class_10_percentage}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mt-6">Class 12th Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Board *</label>
                    <input
                      type="text"
                      name="class_12_board"
                      value={formData.class_12_board}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                    <input
                      type="text"
                      name="class_12_school"
                      value={formData.class_12_school}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year of Passing</label>
                    <input
                      type="text"
                      name="class_12_year"
                      value={formData.class_12_year}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="class_12_percentage"
                      value={formData.class_12_percentage}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                {formData.is_lateral_entry && (
                  <>
                    <h3 className="text-lg font-semibold text-gray-800 mt-6">Diploma Details (for Lateral Entry)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Diploma Name</label>
                        <input
                          type="text"
                          name="diploma_name"
                          value={formData.diploma_name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">College</label>
                        <input
                          type="text"
                          name="diploma_college"
                          value={formData.diploma_college}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Board/University</label>
                        <input
                          type="text"
                          name="diploma_board"
                          value={formData.diploma_board}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Year of Passing</label>
                        <input
                          type="text"
                          name="diploma_year"
                          value={formData.diploma_year}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="YYYY"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage</label>
                        <input
                          type="number"
                          step="0.01"
                          name="diploma_percentage"
                          value={formData.diploma_percentage}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Gap Year Section */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Gap Year Information (if any)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gap Year</label>
                      <input
                        type="text"
                        name="gap_year"
                        value={formData.gap_year}
                        onChange={handleInputChange}
                        placeholder="e.g., 2024"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gap Reason</label>
                      <input
                        type="text"
                        name="gap_reason"
                        value={formData.gap_reason}
                        onChange={handleInputChange}
                        placeholder="Reason for gap"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Medium of Instruction */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Medium of Instruction</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Previous Education Medium</label>
                      <select
                        name="medium_of_instruction"
                        value={formData.medium_of_instruction}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="English">English</option>
                        <option value="Hindi">Hindi</option>
                        <option value="Bengali">Bengali</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Higher Education (Optional) */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Higher Education (Optional)</h3>
                  
                  {/* Graduation */}
                  <div className="mb-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.has_graduation}
                        onChange={(e) => setFormData(prev => ({ ...prev, has_graduation: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">I have completed Graduation</span>
                    </label>
                  </div>
                  
                  {formData.has_graduation && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
                        <input
                          type="text"
                          name="graduation_degree"
                          value={formData.graduation_degree}
                          onChange={handleInputChange}
                          placeholder="e.g., B.Sc, BA, B.Com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">College</label>
                        <input
                          type="text"
                          name="graduation_college"
                          value={formData.graduation_college}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
                        <input
                          type="text"
                          name="graduation_university"
                          value={formData.graduation_university}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Year of Passing</label>
                        <input
                          type="text"
                          name="graduation_year"
                          value={formData.graduation_year}
                          onChange={handleInputChange}
                          placeholder="YYYY"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage</label>
                        <input
                          type="number"
                          step="0.01"
                          name="graduation_percentage"
                          value={formData.graduation_percentage}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Masters */}
                  <div className="mb-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.has_masters}
                        onChange={(e) => setFormData(prev => ({ ...prev, has_masters: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">I have completed Masters/Post-Graduation</span>
                    </label>
                  </div>
                  
                  {formData.has_masters && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
                        <input
                          type="text"
                          name="masters_degree"
                          value={formData.masters_degree}
                          onChange={handleInputChange}
                          placeholder="e.g., M.Sc, MA, MBA"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">College</label>
                        <input
                          type="text"
                          name="masters_college"
                          value={formData.masters_college}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
                        <input
                          type="text"
                          name="masters_university"
                          value={formData.masters_university}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Year of Passing</label>
                        <input
                          type="text"
                          name="masters_year"
                          value={formData.masters_year}
                          onChange={handleInputChange}
                          placeholder="YYYY"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage</label>
                        <input
                          type="number"
                          step="0.01"
                          name="masters_percentage"
                          value={formData.masters_percentage}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mt-6">Parent/Guardian Details</h3>
                
                <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">Father's Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Father's Name</label>
                    <input
                      type="text"
                      name="father_name"
                      value={formData.father_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Father's Phone</label>
                    <input
                      type="tel"
                      name="father_phone"
                      value={formData.father_phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Father's Email</label>
                    <input
                      type="email"
                      name="father_email"
                      value={formData.father_email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Father's Occupation</label>
                    <select
                      name="father_occupation"
                      value={formData.father_occupation}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Occupation</option>
                      <option value="Government Service">Government Service</option>
                      <option value="Private Service">Private Service</option>
                      <option value="Business">Business</option>
                      <option value="Self-Employed">Self-Employed</option>
                      <option value="Farmer/Agriculture">Farmer/Agriculture</option>
                      <option value="Teacher/Professor">Teacher/Professor</option>
                      <option value="Doctor/Medical">Doctor/Medical</option>
                      <option value="Engineer">Engineer</option>
                      <option value="Lawyer/Advocate">Lawyer/Advocate</option>
                      <option value="Bank Employee">Bank Employee</option>
                      <option value="Police/Defence">Police/Defence</option>
                      <option value="Daily Wage Worker">Daily Wage Worker</option>
                      <option value="Retired">Retired</option>
                      <option value="Not Employed">Not Employed</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">Mother's Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Name</label>
                    <input
                      type="text"
                      name="mother_name"
                      value={formData.mother_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Phone</label>
                    <input
                      type="tel"
                      name="mother_phone"
                      value={formData.mother_phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Email</label>
                    <input
                      type="email"
                      name="mother_email"
                      value={formData.mother_email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Occupation</label>
                    <select
                      name="mother_occupation"
                      value={formData.mother_occupation}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Occupation</option>
                      <option value="Homemaker">Homemaker</option>
                      <option value="Government Service">Government Service</option>
                      <option value="Private Service">Private Service</option>
                      <option value="Business">Business</option>
                      <option value="Self-Employed">Self-Employed</option>
                      <option value="Teacher/Professor">Teacher/Professor</option>
                      <option value="Doctor/Medical">Doctor/Medical</option>
                      <option value="Nurse">Nurse</option>
                      <option value="Bank Employee">Bank Employee</option>
                      <option value="Farmer/Agriculture">Farmer/Agriculture</option>
                      <option value="Daily Wage Worker">Daily Wage Worker</option>
                      <option value="Retired">Retired</option>
                      <option value="Not Employed">Not Employed</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">Guardian Information (Primary Contact)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Name</label>
                    <input
                      type="text"
                      name="guardian_name"
                      value={formData.guardian_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Name of primary guardian"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Phone</label>
                    <input
                      type="tel"
                      name="guardian_phone"
                      value={formData.guardian_phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Relation</label>
                    <select
                      name="guardian_relation"
                      value={formData.guardian_relation}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Brother">Brother</option>
                      <option value="Sister">Sister</option>
                      <option value="Uncle">Uncle</option>
                      <option value="Aunt">Aunt</option>
                      <option value="Grandfather">Grandfather</option>
                      <option value="Grandmother">Grandmother</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-between gap-3 mt-6">
                  <Button onClick={handlePrevious} variant="outline">
                    ← Previous
                  </Button>
                  <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700">
                    Next: Upload Documents →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Document Upload */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800">Upload Documents</h2>
                <p className="text-sm text-gray-600">Please upload clear scanned copies or photos of your documents. Fields marked with * are mandatory.</p>
                
                {/* File Size Warning Banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="text-sm font-medium text-amber-800">File Upload Guidelines</p>
                      <ul className="text-xs text-amber-700 mt-1 list-disc list-inside space-y-1">
                        <li><strong>Photo & Signature: Maximum 70 KB</strong></li>
                        <li><strong>Other Documents: Maximum 300 KB</strong></li>
                        <li>Allowed formats: JPG, PNG, WEBP, PDF</li>
                        <li>Tip: If your file is too large, reduce image quality or use a compression tool</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {documentSettingsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="animate-spin text-blue-600" size={32} />
                    <span className="ml-3 text-gray-600">Loading document requirements...</span>
                  </div>
                ) : documentSettings.length > 0 ? (
                  /* Dynamic Documents from Document Settings */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {documentSettings.map((doc) => {
                      const docKey = doc.document_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                      const acceptFormats = doc.allowed_formats?.map(f => {
                        if (f === 'pdf') return 'application/pdf';
                        if (f === 'jpg' || f === 'jpeg') return 'image/jpeg';
                        if (f === 'png') return 'image/png';
                        return `image/${f}`;
                      }).join(',') || 'image/*,application/pdf';
                      const isUploaded = !!formData.documents[docKey];
                      const isImage = formData.documents[docKey]?.startsWith('data:image');
                      
                      const isPhotoOrSignature = docKey.includes('photo') || docKey.includes('signature');
                      // Use tenant's max_size_kb if available, otherwise use defaults (70KB for photo/sig, 300KB for others)
                      const maxSizeKb = doc.max_size_kb || (isPhotoOrSignature ? 70 : 300);
                      const maxSizeLabel = `${maxSizeKb} KB`;
                      
                      return (
                        <div 
                          key={doc.id} 
                          className={`border-2 border-dashed rounded-lg p-6 text-center ${
                            uploadErrors[docKey]
                              ? 'border-red-500 bg-red-50'
                              : doc.is_mandatory 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300 bg-white'
                          }`}
                        >
                          <Upload 
                            className={`mx-auto mb-3 ${doc.is_mandatory ? 'text-red-500' : 'text-gray-400'}`} 
                            size={40} 
                          />
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {doc.document_name} {doc.is_mandatory && '*'}
                          </label>
                          {doc.description && (
                            <p className="text-xs text-gray-500 mb-2">{doc.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mb-2">
                            Max: {maxSizeLabel} | Formats: {doc.allowed_formats?.join(', ').toUpperCase() || 'JPG, PNG, PDF'}
                          </p>
                          
                          {/* Error Message */}
                          {uploadErrors[docKey] && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-xs">
                              <strong>Error:</strong> {uploadErrors[docKey]}
                            </div>
                          )}
                          <input
                            type="file"
                            accept={acceptFormats}
                            onChange={(e) => handleFileUpload(e, docKey, doc)}
                            className="hidden"
                            id={`doc-upload-${docKey}`}
                          />
                          <label 
                            htmlFor={`doc-upload-${docKey}`} 
                            className="cursor-pointer text-blue-600 hover:text-blue-700"
                          >
                            {isUploaded 
                              ? '✓ Uploaded - Click to Replace' 
                              : `Click to Upload ${doc.is_mandatory ? '(Required)' : '(Optional)'}`
                            }
                          </label>
                          {isUploaded && isImage && (
                            <img 
                              src={formData.documents[docKey]} 
                              alt="Preview" 
                              className="mt-3 mx-auto w-32 h-32 object-cover rounded" 
                            />
                          )}
                          {isUploaded && !isImage && (
                            <div className="mt-3 text-green-600 text-sm">📄 File uploaded</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Fallback - Default Documents (Profile Photo and Signature only) */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      uploadErrors['profile_photo'] ? 'border-red-500 bg-red-100' : 'border-red-300 bg-red-50'
                    }`}>
                      <Upload className="mx-auto text-red-500 mb-3" size={40} />
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo *</label>
                      <p className="text-xs text-gray-400 mb-2">Max: 70 KB | Formats: JPG, PNG</p>
                      
                      {/* Error Message */}
                      {uploadErrors['profile_photo'] && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-xs">
                          <strong>Error:</strong> {uploadErrors['profile_photo']}
                        </div>
                      )}
                      
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'profile_photo')}
                        className="hidden"
                        id="photo-upload"
                        required
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer text-blue-600 hover:text-blue-700">
                        {formData.documents.profile_photo ? '✓ Photo Uploaded' : 'Click to Upload (Required)'}
                      </label>
                      {formData.documents.profile_photo && (
                        <img src={formData.documents.profile_photo} alt="Preview" className="mt-3 mx-auto w-32 h-32 object-cover rounded" />
                      )}
                    </div>

                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      uploadErrors['signature'] ? 'border-red-500 bg-red-100' : 'border-red-300 bg-red-50'
                    }`}>
                      <Upload className="mx-auto text-red-500 mb-3" size={40} />
                      <label className="block text-sm font-medium text-gray-700 mb-2">Signature *</label>
                      <p className="text-xs text-gray-400 mb-2">Max: 70 KB | Formats: JPG, PNG</p>
                      
                      {/* Error Message */}
                      {uploadErrors['signature'] && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-xs">
                          <strong>Error:</strong> {uploadErrors['signature']}
                        </div>
                      )}
                      
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'signature')}
                        className="hidden"
                        id="signature-upload"
                        required
                      />
                      <label htmlFor="signature-upload" className="cursor-pointer text-blue-600 hover:text-blue-700">
                        {formData.documents.signature ? '✓ Uploaded' : 'Click to Upload (Required)'}
                      </label>
                      {formData.documents.signature && (
                        <img src={formData.documents.signature} alt="Signature Preview" className="mt-3 mx-auto w-32 h-16 object-contain rounded" />
                      )}
                    </div>

                    <p className="col-span-2 text-center text-amber-600 text-sm bg-amber-50 p-3 rounded-lg">
                      ℹ️ No specific document requirements configured for this course. Please upload at least Profile Photo and Signature.
                    </p>
                  </div>
                )}

                <div className="flex justify-between gap-3 mt-6">
                  <Button onClick={handlePrevious} variant="outline">
                    ← Previous
                  </Button>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {submitting ? (
                      <>
                        <Loader className="animate-spin mr-2" size={16} />
                        Submitting...
                      </>
                    ) : (
                      'Submit Application →'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && applicationData && (
              <div className="space-y-6 text-center">
                <CheckCircle className="mx-auto text-green-500" size={64} />
                <h2 className="text-2xl font-bold text-gray-800">Application Submitted!</h2>
                <p className="text-gray-600">Application Number: <strong>{applicationData.application_number}</strong></p>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Complete Your Seat Booking</h3>
                  <p className="text-3xl font-bold text-blue-700 mb-2">₹{(applicationData.booking_fee || applicationData.payment_amount || applicationData.admission_fee)?.toLocaleString('en-IN')}</p>
                  <p className="text-sm text-gray-600 mb-2">Seat Booking Fee</p>
                  {applicationData.admission_fee && applicationData.booking_fee && applicationData.admission_fee !== applicationData.booking_fee && (
                    <p className="text-xs text-gray-500 mb-4">(Remaining Admission Fee: ₹{(applicationData.admission_fee - applicationData.booking_fee).toLocaleString('en-IN')} - payable at institute)</p>
                  )}
                  
                  <Button
                    onClick={handlePayment}
                    disabled={paymentProcessing}
                    className="bg-blue-600 hover:bg-blue-700 text-lg py-6 px-8"
                  >
                    {paymentProcessing ? (
                      <>
                        <Loader className="animate-spin mr-2" size={20} />
                        Processing Payment...
                      </>
                    ) : (
                      'Pay Now with Razorpay'
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 mt-4">
                    Test Mode: Use card 4111 1111 1111 1111, any future expiry, any CVV
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Success */}
            {step === 5 && applicationData && (
              <div className="space-y-6 text-center">
                <CheckCircle className="mx-auto text-green-500" size={80} />
                <h2 className="text-3xl font-bold text-green-700">
                  {applicationData.payment_required === false ? 'Application Submitted Successfully!' : 'Payment Successful!'}
                </h2>
                <p className="text-gray-600">
                  {applicationData.payment_required === false 
                    ? 'Your admission application has been received. Please visit the institution to complete payment.'
                    : 'Your seat has been booked successfully! Please visit the institution to complete admission.'}
                </p>

                <div className={`p-6 rounded-lg ${applicationData.payment_required === false ? 'bg-blue-50' : 'bg-green-50'}`}>
                  <div className="grid grid-cols-2 gap-4 text-left max-w-md mx-auto">
                    <div>
                      <p className="text-sm text-gray-600">Application Number</p>
                      <p className="font-semibold">{applicationData.application_number}</p>
                    </div>
                    {applicationData.seat_booking_number && (
                      <div>
                        <p className="text-sm text-gray-600">Booking Reference</p>
                        <p className="font-semibold text-blue-600">{applicationData.seat_booking_number}</p>
                      </div>
                    )}
                    {applicationData.payment_required !== false && (
                      <>
                        <div>
                          <p className="text-sm text-gray-600">Receipt Number</p>
                          <p className="font-semibold">{applicationData.receipt_number}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Booking Fee Paid</p>
                          <p className="font-semibold text-green-700">₹{(applicationData.booking_fee || applicationData.payment_amount || applicationData.admission_fee)?.toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Payment Status</p>
                          <p className="font-semibold text-green-700">Success</p>
                        </div>
                        {applicationData.admission_fee && applicationData.booking_fee && applicationData.admission_fee > applicationData.booking_fee && (
                          <div className="col-span-2 bg-yellow-50 p-2 rounded">
                            <p className="text-sm text-yellow-700">
                              <strong>Note:</strong> Balance Admission Fee of ₹{(applicationData.admission_fee - applicationData.booking_fee).toLocaleString('en-IN')} is payable at the institute.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    {applicationData.payment_required === false && (
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <p className="font-semibold text-blue-700">Under Review</p>
                      </div>
                    )}
                  </div>
                  
                  {applicationData.payment_required === false && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> Online payment is currently not enabled. Please contact the institution for payment instructions.
                      </p>
                    </div>
                  )}
                </div>

                {/* Student Portal Login Credentials */}
                {applicationData.generated_student_id && (
                  <div className="bg-blue-50 border-2 border-blue-300 p-6 rounded-lg">
                    <h3 className="text-lg font-bold text-blue-800 mb-4">
                      Your Student Portal Login Credentials
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-left max-w-md mx-auto">
                      <div>
                        <p className="text-sm text-gray-600">Student ID</p>
                        <p className="font-bold text-blue-700 text-lg">{applicationData.generated_student_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Password</p>
                        <p className="font-bold text-blue-700 text-lg">{applicationData.student_password}</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <a 
                        href="/student/login" 
                        target="_blank"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Login to Student Portal
                      </a>
                    </div>
                    <p className="text-xs text-gray-600 mt-3">
                      Save these credentials! You can login to track your admission status.
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <p className="text-sm text-gray-700">
                    <strong>What's Next?</strong><br />
                    Your application will be reviewed by our admission team.<br />
                    You will receive updates on your email: <strong>{formData.email}</strong>
                  </p>
                  
                  <Button
                    onClick={downloadReceipt}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download size={16} className="mr-2" />
                    Download Receipt
                  </Button>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-left">
                  <p className="text-sm text-gray-700">
                    <strong>Important:</strong> Please save your application number, receipt number, and Student Portal credentials for future reference.
                    A copy of the receipt has been sent to your email address.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicAdmissionForm;
